import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalHRPDFExport from '../../components/legal-hr/LegalHRPDFExport';
import toast from 'react-hot-toast';
import { anthropicService } from '../../services/anthropicService';
import { supabase } from '../../lib/supabase';
import { legalHrStorageService, SavedLegalHrDocument } from '../../services/legalHrStorageService';

// Define supported countries for NDA
const supportedCountries = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Define business types for dropdown
const businessTypes = [
  'Technology Company',
  'Retail Business',
  'Professional Services',
  'Healthcare Organization',
  'Manufacturing Company',
  'Hospitality Business',
  'Educational Institution',
  'Non-profit Organization',
  'Financial Services',
  'Creative Agency',
];

// NDA provisions
const ndaProvisions = [
  { 
    id: 'definition', 
    label: 'Definition of Confidential Information', 
    example: 'What information is considered confidential',
    required: true
  },
  { 
    id: 'obligations', 
    label: 'Confidentiality Obligations', 
    example: 'How parties must handle and protect confidential information',
    required: true
  },
  { 
    id: 'exclusions', 
    label: 'Exclusions from Confidentiality', 
    example: 'Information not covered by the NDA (e.g., public information)',
    defaultSelected: true 
  },
  { 
    id: 'term', 
    label: 'Term and Duration', 
    example: 'How long confidential information must be protected',
    defaultSelected: true 
  },
  { 
    id: 'return', 
    label: 'Return of Information', 
    example: 'Procedures for returning confidential information when agreement ends',
    defaultSelected: true
  },
  { 
    id: 'remedies', 
    label: 'Remedies for Breach', 
    example: 'Legal remedies if confidentiality is breached',
    defaultSelected: true 
  },
  { 
    id: 'jurisdiction', 
    label: 'Jurisdiction and Governing Law', 
    example: 'Which laws govern the agreement and where disputes are resolved',
    defaultSelected: true 
  },
  { 
    id: 'noncompete', 
    label: 'Non-Compete Provisions', 
    example: 'Restrictions on competing with the disclosing party',
    defaultSelected: false 
  },
  { 
    id: 'nonsolicitation', 
    label: 'Non-Solicitation Provisions', 
    example: 'Restrictions on soliciting employees, clients, or customers',
    defaultSelected: false 
  },
  { 
    id: 'assignment', 
    label: 'Assignment and Transfer Rights', 
    example: 'Rights to transfer the agreement to other parties',
    defaultSelected: false 
  }
];

const NdaGenerator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [ndaGenerated, setNdaGenerated] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [selectedProvisions, setSelectedProvisions] = useState<string[]>(
    ndaProvisions.filter(provision => provision.defaultSelected || provision.required).map(provision => provision.id)
  );
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [ndaText, setNdaText] = useState('');
  const [latestDocument, setLatestDocument] = useState<SavedLegalHrDocument | null>(null);
  const [ndaType, setNdaType] = useState('mutual');
  const [ndaDuration, setNdaDuration] = useState('2');
  const [disclosingPartyName, setDisclosingPartyName] = useState('');
  const [receivingPartyName, setReceivingPartyName] = useState('');

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const handleProvisionChange = (provisionId: string) => {
    // Don't allow deselection of required provisions
    if (ndaProvisions.find(provision => provision.id === provisionId)?.required) {
      return;
    }
    
    if (selectedProvisions.includes(provisionId)) {
      setSelectedProvisions(selectedProvisions.filter(item => item !== provisionId));
    } else {
      setSelectedProvisions([...selectedProvisions, provisionId]);
    }
  };

  const generateNda = async () => {
    // Validation
    if (ndaType === 'mutual' && (!companyName || !receivingPartyName)) {
      toast.error('For mutual NDAs, please enter both party names');
      return;
    }
    
    if (ndaType === 'oneway' && (!disclosingPartyName || !receivingPartyName)) {
      toast.error('Please enter both disclosing and receiving party names');
      return;
    }
    
    if (!businessType) {
      toast.error('Please select your business type');
      return;
    }
    
    if (!contactEmail) {
      toast.error('Please enter a contact email');
      return;
    }
    
    if (!ndaDuration) {
      toast.error('Please specify the NDA duration');
      return;
    }

    setGenerating(true);
    
    try {
      // Get the country name from the selected country code
      const selectedCountryData = supportedCountries.find(country => country.code === selectedCountry);
      const countryName = selectedCountryData ? selectedCountryData.name : 'United Kingdom';
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Format the selected provisions into a readable list
      const selectedNdaProvisions = ndaProvisions
        .filter(provision => selectedProvisions.includes(provision.id))
        .map(provision => provision.label)
        .join(', ');

      // Create a comprehensive prompt for Anthropic
      const prompt = `
Generate a comprehensive, professional Non-Disclosure Agreement (NDA) for a ${businessType} primarily operating in ${countryName}.

${ndaType === 'mutual' 
  ? `This should be a MUTUAL NDA between "${companyName}" and "${receivingPartyName}".` 
  : `This should be a ONE-WAY NDA with "${disclosingPartyName}" as the disclosing party and "${receivingPartyName}" as the receiving party.`}

The agreement should include the following sections: ${selectedNdaProvisions}

Important details:
- Primary jurisdiction: ${countryName}
- Contact email for legal inquiries: ${contactEmail}
- Effective date for the NDA: ${effectiveDate}
- Duration of confidentiality obligations: ${ndaDuration} years

Please format the NDA in markdown, with appropriate sections, headings, and comprehensive coverage of all required elements for ${countryName}.

Make sure the NDA complies with legal requirements specific to ${countryName} such as ${
  selectedCountry === 'GB' ? 'UK contract law and trade secret regulations' : 
  selectedCountry === 'US' ? 'Uniform Trade Secrets Act and/or Defend Trade Secrets Act' : 
  selectedCountry === 'EU' ? 'EU Trade Secrets Directive' : 
  selectedCountry === 'CA' ? 'Canadian intellectual property and confidential information laws' : 
  selectedCountry === 'AU' ? 'Australian confidential information laws' : 
  'relevant trade secret and confidentiality laws'
}.

The NDA should be written in clear, legally sound language and formatted for professional use. Include appropriate signature blocks at the end.
`;

      // Make the actual API call to Anthropic through our service
      const response = await anthropicService.generateText(prompt);
      
      setNdaText(response);
      setLatestDocument(null);
      setNdaGenerated(true);
      toast.success('Non-Disclosure Agreement generated successfully!');
    } catch (error) {
      console.error('Error generating NDA:', error);
      toast.error('Failed to generate Non-Disclosure Agreement. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (ndaText) {
      navigator.clipboard.writeText(ndaText)
        .then(() => toast.success('NDA copied to clipboard'))
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy text');
        });
    } else {
      toast.error('No content to copy');
    }
  };

  const handlePdfExportComplete = async ({ blob }: { filename: string; blob: Blob }) => {
    if (!user?.id) {
      toast.error('You must be logged in to save the document');
      return;
    }

    try {
      const saved = await legalHrStorageService.saveLegalHrDocument({
        userId: user.id,
        docType: 'nda',
        blob,
        baseName: companyName || disclosingPartyName || receivingPartyName || 'nda',
        effectiveDate,
        metadata: {
          companyName,
          businessType,
          contactEmail,
          ndaType,
          ndaDuration,
          jurisdiction: selectedCountry,
          disclosingPartyName: ndaType === 'oneway' ? disclosingPartyName : companyName,
          receivingPartyName,
          selectedProvisions,
        },
        tags: [
          `jurisdiction:${selectedCountry}`,
          `nda-type:${ndaType}`,
          `duration:${ndaDuration}`,
        ],
      });

      setLatestDocument(saved);
      toast.success(`NDA saved to workspace as version v${saved.version}`);
    } catch (error) {
      console.error('Failed to upload NDA PDF', error);
      toast.error('Failed to save NDA to storage');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-600">
            Non-Disclosure Agreement Generator
          </h1>
          <p className="mt-3 text-xl text-gray-600 max-w-2xl mx-auto">
            Create a customized NDA to protect your confidential information when sharing with third parties.
          </p>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {!ndaGenerated ? (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column - Party Info */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">NDA Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="ndaType" className="block text-sm font-medium text-gray-700 mb-1">
                        Type of NDA*
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setNdaType('mutual')}
                          className={`px-4 py-2 text-center rounded-md border ${
                            ndaType === 'mutual' 
                              ? 'border-blue-500 bg-blue-50 text-blue-700' 
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          Mutual NDA
                          <div className="text-xs text-gray-500 mt-1">Both parties share confidential info</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNdaType('oneway')}
                          className={`px-4 py-2 text-center rounded-md border ${
                            ndaType === 'oneway' 
                              ? 'border-blue-500 bg-blue-50 text-blue-700' 
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          One-Way NDA
                          <div className="text-xs text-gray-500 mt-1">Only one party shares confidential info</div>
                        </button>
                      </div>
                    </div>
                    
                    {ndaType === 'mutual' ? (
                      <>
                        <div>
                          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                            First Party Name*
                          </label>
                          <input
                            type="text"
                            id="companyName"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Your Company Ltd"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label htmlFor="receivingPartyName" className="block text-sm font-medium text-gray-700 mb-1">
                            Second Party Name*
                          </label>
                          <input
                            type="text"
                            id="receivingPartyName"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Other Company Ltd"
                            value={receivingPartyName}
                            onChange={(e) => setReceivingPartyName(e.target.value)}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label htmlFor="disclosingPartyName" className="block text-sm font-medium text-gray-700 mb-1">
                            Disclosing Party Name*
                          </label>
                          <input
                            type="text"
                            id="disclosingPartyName"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Disclosing Company Ltd"
                            value={disclosingPartyName}
                            onChange={(e) => setDisclosingPartyName(e.target.value)}
                          />
                        </div>
                        <div>
                          <label htmlFor="receivingPartyName" className="block text-sm font-medium text-gray-700 mb-1">
                            Receiving Party Name*
                          </label>
                          <input
                            type="text"
                            id="receivingPartyName"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Receiving Company Ltd"
                            value={receivingPartyName}
                            onChange={(e) => setReceivingPartyName(e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type*
                      </label>
                      <select
                        id="businessType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      >
                        <option value="">Select a business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email*
                      </label>
                      <input
                        type="email"
                        id="contactEmail"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="legal@example.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        Governing Jurisdiction*
                      </label>
                      <select
                        id="country"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                      >
                        {supportedCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective Date
                      </label>
                      <input
                        type="date"
                        id="effectiveDate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="ndaDuration" className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (Years)*
                      </label>
                      <select
                        id="ndaDuration"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={ndaDuration}
                        onChange={(e) => setNdaDuration(e.target.value)}
                      >
                        <option value="1">1 year</option>
                        <option value="2">2 years</option>
                        <option value="3">3 years</option>
                        <option value="5">5 years</option>
                        <option value="10">10 years</option>
                        <option value="perpetual">Perpetual</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Right column - NDA Provisions */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">NDA Provisions</h2>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select provisions to include in your NDA:
                    </label>
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      {ndaProvisions.map((provision) => (
                        <div key={provision.id} className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id={provision.id}
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              checked={selectedProvisions.includes(provision.id)}
                              onChange={() => handleProvisionChange(provision.id)}
                              disabled={provision.required}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor={provision.id} className="font-medium text-gray-700 flex items-center">
                              {provision.label}
                              {provision.required && (
                                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                  Required
                                </span>
                              )}
                            </label>
                            <p className="text-gray-500">{provision.example}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={generateNda}
                  disabled={generating}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Generate NDA'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="mb-6 flex justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generated Non-Disclosure Agreement</h2>
                <div className="space-x-2">
                  <button 
                    onClick={handleCopyToClipboard}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Copy to Clipboard
                  </button>
                  {ndaText && (
                    <LegalHRPDFExport 
                      contentRef={contentRef} 
                      filename="non-disclosure-agreement.pdf"
                      title={companyName ? `${companyName} - Non-Disclosure Agreement` : "Non-Disclosure Agreement"}
                      documentType="Non-Disclosure Agreement"
                      onExportComplete={handlePdfExportComplete}
                    />
                  )}
                  <button 
                    onClick={() => setNdaGenerated(false)}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              {/* Display the generated NDA in a nicely formatted way */}
              <div className="bg-white p-6 rounded-lg shadow-md" ref={contentRef}>
                <div dangerouslySetInnerHTML={{ __html: ndaText }} />
              </div>
              
              <div className="mt-8 text-center">
                {latestDocument?.signedUrl && (
                  <div className="mb-4">
                    <a
                      href={latestDocument.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      View saved NDA (v{latestDocument.version})
                    </a>
                  </div>
                )}
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mb-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Legal Disclaimer:</strong> This Non-Disclosure Agreement is generated by AI and should be reviewed by a qualified legal professional before use. Laws regarding confidential information and trade secrets vary by jurisdiction and may change over time. The generated document may not cover all legal requirements specific to your business or jurisdiction.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setNdaGenerated(false);
                    window.scrollTo(0, 0);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Generate Another NDA
                </button>
              </div>
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 border-t border-yellow-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Legal Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This generator provides a template only and is not a substitute for professional legal advice. 
                    The generated NDA should be reviewed by qualified legal professionals before signing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NdaGenerator;
